import { useEffect, useState } from "react";

type Breakpoints =
  | "sm"
  | "md"
  | "lg"
  | "mx"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | { customMediaQuery: string };

// Convert breakpoints to actual CSS media query string
const getMediaQuery = (breakpoint: Breakpoints): string => {
  if (typeof breakpoint === "string") {
    switch (breakpoint) {
      case "sm":
        return "(max-width: 819px)";
      case "md":
        return "(min-width: 820px) and (max-width: 1024px)";
      case "lg":
        return "(min-width: 1025px) and (max-width: 1280px)";
      case "xl":
        return "(min-width: 1281px) and (max-width: 1440px)";
      case "2xl":
        return "(min-width: 1441px) and (max-width: 1920px)";
      case "3xl":
        return "(min-width: 1921px) and (max-width: 2560px)";
      case "4xl":
        return "(min-width: 2561px)";
      default:
        throw new Error(`Unknown breakpoint: ${breakpoint}`);
    }
  } else {
    return breakpoint.customMediaQuery;
  }
};

export const useMediaQueryMatch = (breakpoint: Breakpoints): boolean => {
  const [isMediaMatch, setIsMediaMatch] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(getMediaQuery(breakpoint))?.matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return null;
    }
    const mediaQueryList = window.matchMedia(getMediaQuery(breakpoint));
    const handleMediaQueryChange = (e: MediaQueryListEvent): void => {
      setIsMediaMatch(e.matches);
    };
    if (mediaQueryList) {
      setIsMediaMatch(mediaQueryList.matches);

      mediaQueryList.addEventListener("change", handleMediaQueryChange);
    }
    return () => {
      mediaQueryList?.removeEventListener("change", handleMediaQueryChange);
    };
  }, [breakpoint]);

  return isMediaMatch;
};
